import { reactivityKeys } from '@vs-shop/engine/atom/reactivityKeys';
import addCouponAndProductToOrder from '../../flow/order/addCouponAndProductToOrder';
import addCouponToOrder from '../../flow/order/addCouponToOrder';
import { Effect, Option } from 'effect';
import * as Atom from 'effect/unstable/reactivity/Atom';

import {
  type AddProductVariantToOrderDataProduct,
  trackProductVariantAddedToOrder,
  writeProductVariantToOrder,
} from '../../flow/order/addProductVariantToOrder';
import processOrder from '../../flow/order/processOrder';
import removeCouponFromOrder from '../../flow/order/removeCouponFromOrder';
import removeProductVariantFromOrder from '../../flow/order/removeProductVariantFromOrder';
import setOrderShipment from '../../flow/order/setOrderShipment';
import updateOrderItemQuantity from '../../flow/order/updateOrderItemQuantity';
import { makeCartMutations } from './cartMutations';
import { RuntimeClientAtom } from '@vs-shop/engine/runtime/RuntimeClient';
import { CartClientAtom } from '../../runtime/layers/ClientLayer';

const cartMutations = makeCartMutations({
  describe: (product: AddProductVariantToOrderDataProduct) => ({
    variantCode: product.code,
    productName: product.name,
    unitPrice: product.price,
  }),
  write: (request, quantityInCart) =>
    writeProductVariantToOrder({
      product: request.product,
      quantity: request.quantity,
      quantityInCart,
    }),
  track: (request) =>
    trackProductVariantAddedToOrder(request.product, request.quantity).pipe(Effect.ignore),
});

export const addCouponToOrderAtomFn = CartClientAtom.fn(cartMutations.write(addCouponToOrder));

export const addCouponWithProductVariantsToOrderAtomFn = CartClientAtom.fn(
  cartMutations.write(addCouponAndProductToOrder)
);

/**
 * Optimistic add-to-cart for one line or several. It refreshes the cart atom
 * itself, on success and on failure, so it carries no `reactivityKeys`: those
 * refresh on success only.
 */
export const addToCartAtomFn = CartClientAtom.fn(cartMutations.add);

export const processOrderAtomFn = RuntimeClientAtom.fn(cartMutations.serialize(processOrder), {
  reactivityKeys: [reactivityKeys.orderToken],
});

export const removeCouponFromOrderAtomFn = CartClientAtom.fn(
  cartMutations.write((_data: Record<string, never>) => removeCouponFromOrder)
);

/**
 * One removal function per cart line. `Atom.fn` settles an earlier call with
 * the outcome of the latest one, so a shared function would report one line's
 * result to another line's button.
 */
export const removeProductVariantFromOrderAtomFn = Atom.family((_cartItemId: string) =>
  CartClientAtom.fn(
    cartMutations.writeLine(removeProductVariantFromOrder, (data) =>
      Option.some(data.cartItem.event.variantCode)
    )
  )
);

// Writes cookies and emits GTM events; it never touches the order API, so it
// stays outside the cart mutation queue.
export const setOrderShipmentAtomFn = RuntimeClientAtom.fn(setOrderShipment, {
  reactivityKeys: [reactivityKeys.orderShipment],
});

/** One quantity function per cart line, for the reason the removal has one. */
export const updateOrderItemQuantityAtomFn = Atom.family((_cartItemId: string) =>
  CartClientAtom.fn(
    cartMutations.writeLine(updateOrderItemQuantity, (data) =>
      Option.some(data.cartItem.event.variantCode)
    )
  )
);
